import { CloseOutlined, PhoneOutlined } from '@ant-design/icons';
import { Button, Tooltip, message } from 'antd';
import PropTypes from 'prop-types';
import React, { useEffect, useRef, useState } from 'react';
import { useAuth } from '../../providers/auth';

// Confirm the `partner` identifier value with CloudTalk support. It labels our
// ERP as the embedding application; the widget itself needs no ERP credentials.
const CLOUDTALK_PHONE_URL = 'https://phone.cloudtalk.io?partner=leoprinting';
const CLOUDTALK_ORIGIN = 'https://phone.cloudtalk.io';
const MINIMIZED_KEY = 'cloudtalk-widget-minimized';
const EXPAND_EVENT = 'cloudtalk-phone:expand';
const MAX_RETRIES = 3;
const RETRY_DELAY_MS = 10000;

const isAuthenticated = () => {
    try {
        return !!localStorage.getItem('USER');
    } catch {
        return false;
    }
};

const BADGE_POS_KEY = 'cloudtalk-badge-pos';
const MODAL_POS_KEY = 'cloudtalk-modal-pos';

const getInitialBadgePos = () => {
    try {
        const saved = JSON.parse(localStorage.getItem(BADGE_POS_KEY));
        if (saved && typeof saved.x === 'number' && typeof saved.y === 'number') {
            const maxX = typeof window !== 'undefined' ? window.innerWidth - 70 : 600;
            const maxY = typeof window !== 'undefined' ? window.innerHeight - 70 : 600;
            return {
                x: Math.min(Math.max(10, saved.x), maxX),
                y: Math.min(Math.max(10, saved.y), maxY),
            };
        }
    } catch {
        // fallback
    }
    return {
        x: typeof window !== 'undefined' ? Math.max(10, window.innerWidth - 75) : 600,
        y: typeof window !== 'undefined' ? Math.max(10, window.innerHeight - 75) : 600,
    };
};

const getInitialModalPos = () => {
    try {
        const saved = JSON.parse(localStorage.getItem(MODAL_POS_KEY));
        if (saved && typeof saved.x === 'number' && typeof saved.y === 'number') {
            const maxX = typeof window !== 'undefined' ? window.innerWidth - 430 : 600;
            const maxY = typeof window !== 'undefined' ? window.innerHeight - 80 : 600;
            return {
                x: Math.min(Math.max(10, saved.x), maxX),
                y: Math.min(Math.max(10, saved.y), maxY),
            };
        }
    } catch {
        // fallback
    }
    return {
        x: typeof window !== 'undefined' ? Math.max(10, window.innerWidth - 440) : 600,
        y: typeof window !== 'undefined' ? Math.max(10, window.innerHeight - 760) : 50,
    };
};

// Persistent embedded CloudTalk softphone: the agent's always-on audio device
// inside the ERP. Mount ONCE inside TelephonyProvider (never unmounts on
// navigation). The iframe stays mounted but hidden when minimized so the
// CloudTalk session — and availability — persists in the background. ERP calls
// ring here; mute/hold work here. The ERP only hosts the frame and listens to
// call events via postMessage (ringing, hangup, ended).
// Other components can force-expand it via:
//   window.dispatchEvent(new Event('cloudtalk-phone:expand'))
// pendingOutbound = { to_number, label, at } of the last ERP-initiated dial,
// so the agent-leg callback ring can be labeled as such (see below).
const CloudTalkPhone = ({ onCallEnded, pendingOutbound, hasAgent: hasAgentProp }) => {
    const { user } = useAuth();
    const hasAgent = hasAgentProp !== undefined ? Boolean(hasAgentProp) : Boolean(user?.cloudtalk_agent_id);
    const [authed, setAuthed] = useState(isAuthenticated);
    const [minimized, setMinimized] = useState(() => {
        try {
            return localStorage.getItem(MINIMIZED_KEY) !== '0';
        } catch {
            return true;
        }
    });

    const [badgePos, setBadgePos] = useState(getInitialBadgePos);
    const [modalPos, setModalPos] = useState(getInitialModalPos);
    const [dragTarget, setDragTarget] = useState(null); // 'badge' | 'modal' | null
    const dragRef = useRef({ startX: 0, startY: 0, originX: 0, originY: 0, moved: false, lastPos: null });

    const [failed, setFailed] = useState(false);
    const [frameKey, setFrameKey] = useState(0);
    const [retryCount, setRetryCount] = useState(0);
    const [ringing, setRinging] = useState(false);
    const [inCall, setInCall] = useState(false);
    const [activeCaller, setActiveCaller] = useState(null);
    const [callSeconds, setCallSeconds] = useState(0);
    // True once any message arrives from the widget origin: proves the frame
    // loaded and is communicating (agent still needs to be logged in + Available
    // inside the widget — cross-origin session state is unreadable by design).
    const [online, setOnline] = useState(false);
    const onCallEndedRef = useRef(onCallEnded);
    onCallEndedRef.current = onCallEnded;

    // Call duration timer
    useEffect(() => {
        let interval = null;
        if (inCall && !ringing) {
            interval = setInterval(() => {
                setCallSeconds((s) => s + 1);
            }, 1000);
        } else {
            setCallSeconds(0);
        }
        return () => {
            if (interval) clearInterval(interval);
        };
    }, [inCall, ringing]);

    useEffect(() => {
        const refresh = () => setAuthed(isAuthenticated());
        window.addEventListener('focus', refresh);
        window.addEventListener('storage', refresh);
        return () => {
            window.removeEventListener('focus', refresh);
            window.removeEventListener('storage', refresh);
        };
    }, []);

    useEffect(() => {
        if (!authed) {
            setOnline(false);
            setRinging(false);
            setInCall(false);
            setActiveCaller(null);
        }
    }, [authed]);

    // External expand hook (e.g. direct-call failures asking for login).
    useEffect(() => {
        const expand = () => setMinimized(false);
        window.addEventListener(EXPAND_EVENT, expand);
        return () => window.removeEventListener(EXPAND_EVENT, expand);
    }, []);

    // When an outbound call attempt starts, mark as active and auto-expand
    useEffect(() => {
        if (pendingOutbound?.at) {
            setInCall(true);
            setActiveCaller(pendingOutbound.label || pendingOutbound.to_number || 'Calling...');
            setMinimized(false);
        }
    }, [pendingOutbound]);

    useEffect(() => {
        if (!authed) return;
        const handleMessage = (event) => {
            if (event.origin !== CLOUDTALK_ORIGIN) return;
            let payload = event.data;
            if (typeof payload === 'string') {
                try {
                    payload = JSON.parse(payload);
                } catch {
                    return;
                }
            }
            if (!payload || typeof payload !== 'object') return;
            setOnline(true);
            const { event: name, properties } = payload;
            if (
                name === 'ringing' ||
                name === 'call_ringing' ||
                name === 'dialing' ||
                name === 'call_dialing' ||
                name === 'outgoing_call'
            ) {
                setRinging(true);
                setInCall(true);
                setMinimized(false);
                // CloudTalk dials outbound calls as a callback: it rings the
                // AGENT first, which looks like an "incoming" call in the
                // widget. If it matches our own recent dial (number + 90s
                // window), say so — green accept connects to the customer.
                const digits = (s) => String(s || '').replace(/\D/g, '');
                const ringDigits = digits(properties?.external_number);
                const wantDigits = digits(pendingOutbound?.to_number);
                const fresh = pendingOutbound?.at && Date.now() - pendingOutbound.at < 90000;
                const isOwnCallback =
                    fresh &&
                    wantDigits &&
                    ringDigits &&
                    (ringDigits === wantDigits ||
                        ringDigits.endsWith(wantDigits.slice(-7)) ||
                        wantDigits.endsWith(ringDigits.slice(-7)));
                if (isOwnCallback) {
                    const who = pendingOutbound.label || properties?.external_number || 'customer';
                    setActiveCaller(who);
                    message.success(`Answer to connect to ${who}`, 6);
                } else {
                    const who = properties?.contact?.name || properties?.external_number || 'Unknown caller';
                    setActiveCaller(who);
                    message.info(`Incoming call: ${who}`);
                }
            } else if (
                name === 'answered' ||
                name === 'talking' ||
                name === 'call_started' ||
                name === 'connected' ||
                name === 'call_connected'
            ) {
                setRinging(false);
                setInCall(true);
                if (properties?.contact?.name || properties?.external_number) {
                    setActiveCaller(properties?.contact?.name || properties?.external_number);
                }
            } else if (
                name === 'hangup' ||
                name === 'ended' ||
                name === 'call_ended' ||
                name === 'call_hangup' ||
                name === 'call_dropped'
            ) {
                setRinging(false);
                setInCall(false);
                setActiveCaller(null);
                setCallSeconds(0);
                onCallEndedRef.current?.(properties || {});
            }
        };
        window.addEventListener('message', handleMessage);
        return () => window.removeEventListener('message', handleMessage);
    }, [authed, pendingOutbound]);

    // Start dragging badge
    const handleBadgeDragStart = (e) => {
        if (e.button !== 0) return;
        setDragTarget('badge');
        dragRef.current = {
            startX: e.clientX,
            startY: e.clientY,
            originX: badgePos.x,
            originY: badgePos.y,
            moved: false,
            lastPos: null,
        };
    };

    // Start dragging modal header
    const handleModalDragStart = (e) => {
        if (e.button !== 0) return;
        if (e.target.closest('button') || e.target.closest('.no-drag')) return;
        setDragTarget('modal');
        dragRef.current = {
            startX: e.clientX,
            startY: e.clientY,
            originX: modalPos.x,
            originY: modalPos.y,
            moved: false,
            lastPos: null,
        };
    };

    // Drag move & drop in any 360-degree direction (up, down, left, right)
    useEffect(() => {
        if (!dragTarget) return;

        const handleMouseMove = (e) => {
            const dx = e.clientX - dragRef.current.startX;
            const dy = e.clientY - dragRef.current.startY;
            if (Math.abs(dx) > 3 || Math.abs(dy) > 3) {
                dragRef.current.moved = true;
            }

            if (dragTarget === 'badge') {
                const maxX = Math.max(10, window.innerWidth - 220);
                const maxY = Math.max(10, window.innerHeight - 60);
                const newX = Math.min(Math.max(10, dragRef.current.originX + dx), maxX);
                const newY = Math.min(Math.max(10, dragRef.current.originY + dy), maxY);
                setBadgePos({ x: newX, y: newY });
                dragRef.current.lastPos = { x: newX, y: newY };
            } else if (dragTarget === 'modal') {
                const maxX = Math.max(10, window.innerWidth - 430);
                const maxY = Math.max(10, window.innerHeight - 60);
                const newX = Math.min(Math.max(10, dragRef.current.originX + dx), maxX);
                const newY = Math.min(Math.max(10, dragRef.current.originY + dy), maxY);
                setModalPos({ x: newX, y: newY });
                dragRef.current.lastPos = { x: newX, y: newY };
            }
        };

        const handleMouseUp = () => {
            const target = dragTarget;
            const wasMoved = dragRef.current.moved;
            setDragTarget(null);

            if (wasMoved) {
                if (dragRef.current.lastPos) {
                    try {
                        const storageKey = target === 'badge' ? BADGE_POS_KEY : MODAL_POS_KEY;
                        localStorage.setItem(storageKey, JSON.stringify(dragRef.current.lastPos));
                    } catch {
                        // ignore storage failures
                    }
                }
            } else if (target === 'badge') {
                // Explicit click on floating widget: re-expand modal
                setMinimized(false);
                try {
                    localStorage.setItem(MINIMIZED_KEY, '0');
                } catch {
                    // ignore
                }
            }
        };

        window.addEventListener('mousemove', handleMouseMove, { passive: true });
        window.addEventListener('mouseup', handleMouseUp);
        return () => {
            window.removeEventListener('mousemove', handleMouseMove);
            window.removeEventListener('mouseup', handleMouseUp);
        };
    }, [dragTarget]);

    // Resilient reload: retry a few times with a delay before giving up. When
    // given up, render nothing — the ERP callback flow keeps working.
    const handleFrameError = () => {
        if (retryCount < MAX_RETRIES) {
            setRetryCount((c) => c + 1);
            setTimeout(() => setFrameKey((k) => k + 1), RETRY_DELAY_MS);
        } else {
            setFailed(true);
        }
    };

    const toggleMinimized = () => {
        setMinimized((prev) => {
            try {
                localStorage.setItem(MINIMIZED_KEY, prev ? '0' : '1');
            } catch {
                // ignore storage failures (private mode)
            }
            return !prev;
        });
    };

    // Format mm:ss
    const formatDuration = (secs) => {
        const m = Math.floor(secs / 60);
        const s = secs % 60;
        return `${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
    };

    // Not logged in (e.g. login page), no agent assigned to user, or the frame cannot load:
    // render nothing.
    if (!authed || !hasAgent || failed) return null;

    const hasActiveCall = inCall || ringing || Boolean(pendingOutbound);
    const statusColor = ringing ? '#16a34a' : online ? '#10b981' : '#94a3b8';
    const isDragging = Boolean(dragTarget);

    return (
        <>
            {/* Full viewport drag overlay to prevent iframe pointer events from interfering with drag in any direction */}
            {isDragging && (
                <div
                    style={{
                        position: 'fixed',
                        inset: 0,
                        zIndex: 999999,
                        cursor: 'grabbing',
                        userSelect: 'none',
                        background: 'transparent',
                    }}
                />
            )}

            {/* ONLY show the floating widget if the modal is closed WHILE a call is active/ringing */}
            {minimized && hasActiveCall && (
                <div
                    onMouseDown={handleBadgeDragStart}
                    style={{
                        position: 'fixed',
                        left: `${badgePos.x}px`,
                        top: `${badgePos.y}px`,
                        zIndex: 99990,
                        cursor: dragTarget === 'badge' ? 'grabbing' : 'pointer',
                        userSelect: 'none',
                        touchAction: 'none',
                    }}
                >
                    <Tooltip title="Click to expand active call">
                        <div
                            style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 10,
                                padding: '8px 16px',
                                background: 'linear-gradient(135deg, #16a34a 0%, #15803d 100%)',
                                color: '#ffffff',
                                borderRadius: 30,
                                boxShadow: '0 8px 24px rgba(22, 163, 74, 0.45)',
                                border: '2px solid rgba(255, 255, 255, 0.85)',
                                fontWeight: 600,
                                fontSize: 13,
                                transition: 'transform 0.15s ease',
                            }}
                        >
                            <span
                                style={{
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    width: 24,
                                    height: 24,
                                    borderRadius: '50%',
                                    background: 'rgba(255, 255, 255, 0.25)',
                                    animation: ringing ? 'pulse 1s infinite' : 'none',
                                }}
                            >
                                <PhoneOutlined style={{ fontSize: 14 }} />
                            </span>
                            <div style={{ display: 'flex', flexDirection: 'column', lineHeight: 1.2 }}>
                                <span style={{ fontSize: 12, maxWidth: 140, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                    {ringing ? 'Ringing...' : activeCaller || 'In Call'}
                                </span>
                                <span style={{ fontSize: 11, opacity: 0.9, fontWeight: 400 }}>
                                    {ringing ? 'Tap to answer' : formatDuration(callSeconds)}
                                </span>
                            </div>
                        </div>
                    </Tooltip>
                </div>
            )}

            {/* Keep the iframe mounted even when minimized (hidden with display: none,
                not unmounted) so the CloudTalk session stays alive and the agent
                stays online in the background without floating badges cluttering the page when idle. */}
            <div
                style={{
                    position: 'fixed',
                    left: `${modalPos.x}px`,
                    top: `${modalPos.y}px`,
                    zIndex: 99990,
                    width: 420,
                    background: '#fff',
                    borderRadius: 12,
                    boxShadow: '0 16px 48px rgba(0,0,0,0.3)',
                    overflow: 'hidden',
                    display: minimized ? 'none' : 'block',
                }}
            >
                <div
                    onMouseDown={handleModalDragStart}
                    style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        padding: '8px 12px',
                        background: '#0f172a',
                        color: '#fff',
                        cursor: dragTarget === 'modal' ? 'grabbing' : 'grab',
                        userSelect: 'none',
                        touchAction: 'none',
                    }}
                >
                    <span style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, fontWeight: 600 }}>
                        <PhoneOutlined /> CloudTalk Phone
                        <span style={{ color: statusColor, fontSize: 11 }}>
                            {ringing ? '● ringing' : online ? '● online' : '● initialized'}
                        </span>
                        {inCall && !ringing && (
                            <span style={{ color: '#4ade80', fontSize: 11, marginLeft: 4, fontWeight: 500 }}>
                                ({formatDuration(callSeconds)})
                            </span>
                        )}
                    </span>
                    <div className="no-drag">
                        <Button
                            size="small"
                            type="text"
                            onClick={(e) => {
                                e.stopPropagation();
                                setFrameKey((k) => k + 1);
                            }}
                            style={{ color: '#fff' }}
                        >
                            Reload
                        </Button>
                        <Tooltip title={hasActiveCall ? 'Minimize to floating widget' : 'Close phone'}>
                            <Button
                                size="small"
                                type="text"
                                icon={<CloseOutlined />}
                                onClick={(e) => {
                                    e.stopPropagation();
                                    toggleMinimized();
                                }}
                                style={{ color: '#fff' }}
                            />
                        </Tooltip>
                    </div>
                </div>
                {!online && (
                    <div style={{ padding: '8px 12px', fontSize: 12, color: '#64748b', background: '#f8fafc' }}>
                        Initialized in the background. If the phone below asks you to log in, please do so to go online.
                    </div>
                )}
                <iframe
                    key={frameKey}
                    src={CLOUDTALK_PHONE_URL}
                    allow="microphone *"
                    title="CloudTalk Phone"
                    width="420"
                    height="700"
                    style={{
                        border: 0,
                        display: 'block',
                        pointerEvents: isDragging ? 'none' : 'auto',
                    }}
                    onError={handleFrameError}
                />
            </div>
        </>
    );
};

CloudTalkPhone.propTypes = {
    hasAgent: PropTypes.bool,
    onCallEnded: PropTypes.func,
    pendingOutbound: PropTypes.object,
};

export default CloudTalkPhone;
